import clsx from 'clsx';
import Autocomplete, { type Config as AutocompleteConfig } from 'bootstrap5-autocomplete';
import { addDays, differenceInCalendarDays, differenceInHours, differenceInMinutes, format, parseISO } from 'date-fns';
import { useFormik } from 'formik';
import { get, intersection, isNil, isNull, omit, size } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import * as Yup from 'yup';
import { toAbsoluteUrl } from '../../../_library/helpers';
import { PageLink, PageTitle } from '../../../_library/layout/core';
import { SkeDialogDelete } from '../../../common/components/dialog-delete';
import { DateFormatUsingDateFns } from '../../../common/consts/DateFormat.const';
import { parseYmdDatesToDateObject } from '../../../common/utilities/date.utility';
import { Company } from '../../../features/company/models/company.model';
import { RootState } from '../../../setup';
import { UserModel, UserProfileTag, UserRoleEnum } from '../../models/UserModel';
import {
	getCompanies,
	getUser,
	getUserAliases,
	getUsers,
	inviteSuperadmin,
	inviteSupervisor,
	linkUserAlias,
	replicateUserToNewInstance,
	sendForgotPassword,
	setUserAvatar,
	unlinkUserAlias,
	updateUser,
} from '../../modules/CRUD/CRUD';
import { UserAsAlias } from '../../../features/user/models/user-alias.model';
import { extractReadableApiError } from '../../../common/api/common.api';

const editUserBreadCrumbs: Array<PageLink> = [
	{
		title: 'Users',
		path: '',
		isSeparator: false,
		isActive: false,
	},
	{
		title: '',
		path: '',
		isSeparator: true,
		isActive: false,
	},
];

type RouteParams = {
	userId: string
}

type UserAliasRow = {
	user: UserModel;
	is_master: boolean;
	is_active: boolean;
	activated_at: string | null;
}

type UserAliasesResponse = {
	masterUserId: number;
	active_id?: number;
	rows: UserAliasRow[];
}

type AliasAutocompleteItem = {
	label: string;
	value: string;
	userId: number;
	companyName: string;
	email: string;
	html: string;
}

const escapeHtml = (value?: string | null) =>
	(value || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');

const getUserEmailSearchSeed = (email?: string | null) => {
	const [localPart = ''] = (email || '').toLowerCase().split('@');
	return localPart.split('+')[0].replace(/\.$/, '').trim();
}

const getComparableEmailSeed = (email?: string | null) =>
	getUserEmailSearchSeed(email).replace(/\.+$/, '');

function EditUser() {
	const signedInUser: UserModel = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel;
	const { userId } = useParams<RouteParams>();
	invariant(userId, 'user id falsy in this route');
	const navigate = useNavigate();
	const token: string = useSelector<RootState>(({ auth }) => auth.accessToken, shallowEqual) as string;
	const [loading, setLoading] = useState(false);
	const [avatarFile, setAvatarFile] = useState<any>(null);
	const [avatar, setAvatar] = useState('');
	const [user, setUser] = useState<UserModel | null>(null);
	const [initialValues, setInitialValues] = useState({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		isEmailVerified: false,
		isLockedOut: false,
		role: '',
		password: '',
	});
	const [newAliasLocationIdentifier, setNewAliasLocationIdentifier] = useState<string>('');
	const [newLocationTenantId, setNewLocationTenantId] = useState<string>();
	const [newAliasPrefix, setNewAliasPrefix] = useState<string>();
	const [newAliasSuffix, setNewAliasSuffix] = useState<string>();
	const [newAliasFull, setNewAliasFull] = useState<string>();
	// comma-delimited string of UserRoleEnum
	const [newAliasRoles, setNewAliasRoles] = useState<string>();

	const [allCompanies, setAllCompanies] = useState<Company[]>([]);
	const [aliasesExpanded, setAliasesExpanded] = useState<boolean>(true);
	const [aliasesLoading, setAliasesLoading] = useState<boolean>(false);
	const [aliases, setAliases] = useState<UserAsAlias[]>([]);
	const [aliasToUnlink, setAliasToUnlink] = useState<UserAsAlias | null>(null);
	const [showUnlinkAliasDialog, setShowUnlinkAliasDialog] = useState<boolean>(false);
	const [selectedAliasProfile, setSelectedAliasProfile] = useState<AliasAutocompleteItem | null>(null);
	const [aliasLinking, setAliasLinking] = useState<boolean>(false);
	const [aliasSearchError, setAliasSearchError] = useState<string>('');
	const aliasAutocompleteRef = useRef<HTMLInputElement | null>(null);
	const aliasAutocompleteInstanceRef = useRef<Autocomplete | null>(null);
	const aliasAutocompletePrimedRef = useRef<boolean>(false);
	const [masterId, setMasterId] = useState<number>();

	const [hasMinCharacters, setHasMinCharacters] = useState<boolean>(false);
	const [hasMinNumbers, setHasMinNumbers] = useState<boolean>(false);
	const [hasSpecialCharacters, setHasSpecialCharacters] = useState<boolean>(false);
	const [hasUppercase, setHasUppercase] = useState<boolean>(false);
	const [hasLowercase, setHasLowercase] = useState<boolean>(false);

	useEffect(() => {
		getCompanies(token)
			.then(({ data }) => {
				const sortedCompanies = (data.items || [])
					.slice()
					.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
				setAllCompanies(sortedCompanies);
			});
	}, [token]);

	const registrationSchema = Yup.object().shape({
		firstName: Yup.string()
									.min(3, 'Minimum 3 symbols')
									.max(50, 'Maximum 50 symbols')
									.required('First name is required'),
		lastName: Yup.string()
								 .min(3, 'Minimum 3 symbols')
								 .max(50, 'Maximum 50 symbols')
								 .required('Last name is required'),
		email: Yup.string(),
		phone: Yup.string().matches(/^\+1\d{10}$/, `Number should be in format +11234567890. Format: + symbol, then country code and number (no symbols/spaces)`).optional(),
		role: Yup.string()
						 .required('Role is required'),
		password: Yup.string()
								.optional()
								.matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, `Password requirements not met`),
	});

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			let reader = new FileReader();
			let file = e.target.files[0];
			setAvatarFile(file);
			reader.onloadend = () => {
				if (reader && reader.result) {
					setAvatar(reader.result as string);
				}
			};
			reader.readAsDataURL(file);
		}
	};

	const removeAvatar = () => {
		setAvatar('');
	};

	const handleClearPasswordChange = () => {
		formik.setFieldValue('password', '', true);
	}

	const formik = useFormik({
		enableReinitialize: true,
		initialValues,
		validationSchema: registrationSchema,
		validateOnChange: true,
		onSubmit: (values) => {
			setLoading(true);
			setTimeout(() => {
				updateUser(
					parseInt(userId),
					{
						...omit(values, 'role'),
						roles: values.role.split(','),
					},
					token,
				)
					.then(({ data }) => {
						if (avatarFile) {
							const requestBody = new FormData();
							requestBody.append('file', avatarFile);
							setUserAvatar(data.id, requestBody, token)
								.then(() => {
									setLoading(false);
									navigate('/users');
								})
								.catch(error => {
									console.error(error);
									setLoading(false);
									navigate('/users');
								});
						} else {
							setLoading(false);
							navigate('/users');
						}
					})
					.catch((err) => {
						console.error(err);
						toast.error(err.response.data.message || 'Error editing user')
						setLoading(false);
					});
			}, 1000);
		},
	});

	useEffect(() => {
		getUser(parseInt(userId), token)
			.then(({ data }) => {
				setUser(data);
				const aliasChecker = data.email.indexOf('+');
				if (aliasChecker > -1) {
					setNewAliasPrefix(`${data.email.slice(0, data.email.indexOf('+'))}+`);
				} else {
					setNewAliasPrefix(`${data.email.slice(0, data.email.indexOf('@'))}+`);
				}
				setNewAliasSuffix(data.email.slice(data.email.indexOf('@')));
				setInitialValues({
					firstName: data.firstName,
					lastName: data.lastName,
					email: data.email,
					phone: data.phone,
					isEmailVerified: data.isEmailVerified,
					isLockedOut: data.isLockedOut,
					role: data.roles.toString(),
					password: '',
				});
			});
	}, [userId, token]);

	const processAliases = (aliasList: UserAsAlias[]) => {

		setAliases(aliasList.sort((a, b) => {
			if (a.is_master) return -1;

			const aName = get(a, 'user.company.name', '').toLowerCase();
			const bName = get(b, 'user.company.name', '').toLowerCase();

			if (aName < bName) return -1;
			// no need to sort if both in same company
			return 1;
		}));
		setMasterId(aliasList.find(a => a.is_master)?.user.id);
	}

	const reloadAliases = useCallback(() => {
		setAliasesLoading(true);
		return getUserAliases(parseInt(userId), token)
			.then(({data}) => {
				if (data.error) {
					throw new Error(get(data, 'msg', 'Failed to load user aliases'));
				}
				const items = get(data, 'results.items', []);
				processAliases(items);
			})
			.catch((err) => {
				setAliases([]);
				console.error('Failed to load aliases', err);
				toast.error(err?.response?.data?.message || 'Failed to load aliases');
			})
			.finally(() => setAliasesLoading(false));
	}, [userId, token]);

	useEffect(() => {
		reloadAliases();
	}, [reloadAliases]);

	const linkedAliasIds = useMemo(() => {
		const ids = new Set<number>();
		aliases.forEach(alias => {
			ids.add(get(alias, 'user.id'));
		});
		return ids;
;	}, [aliases]);

	const aliasSearchSeed = getUserEmailSearchSeed(user?.email);

	const loadAliasCandidates = useCallback(
		async (query: string): Promise<AliasAutocompleteItem[]> => {
			const trimmedQuery = query.trim();
			const normalizedQuery = getComparableEmailSeed(trimmedQuery);

			if (!normalizedQuery) {
				return [];
			}

			try {
				const {data} = await getUsers(
					{
						page: 1,
						limit: 25,
						search: trimmedQuery,
					},
					token,
				);

				const items = ((data as any)?.items || [])
					.filter((candidate: UserModel) => {
						if (size(intersection(candidate.tags, [UserProfileTag.HasAlias, UserProfileTag.SlaveProfile, UserProfileTag.MasterProfile])) > 0) {
							return false;
						}
						const comparableCandidateEmail = getComparableEmailSeed(candidate.email);
						return comparableCandidateEmail.startsWith(normalizedQuery);
					})
					.filter((candidate: UserModel) => !linkedAliasIds.has(candidate.id))
					.map((candidate: UserModel) => {
						const companyName = get(candidate, 'company.name', 'Unknown company');
						return {
							label: candidate.email,
							value: String(candidate.id),
							userId: candidate.id,
							companyName,
							email: candidate.email,
							html: `<div class="d-flex flex-column">
									<span class="fw-bolder">${escapeHtml(companyName)}</span><span class="fw-light small">${escapeHtml(candidate.email)}
									</span>
								</div>`,
						};
					});

				return items;
			} catch (err: any) {
				console.error('Failed to load matching profiles', err);
				return [];
			}
		},
		[token],
		// [linkedAliasIds, token],
	);

	useEffect(() => {
		const input = aliasAutocompleteRef.current;

		if (!input || !aliases) {
			aliasAutocompleteInstanceRef.current?.dispose();
			aliasAutocompleteInstanceRef.current = null;
			return;
		}

		const options: Partial<AutocompleteConfig> = {
			highlightTyped: true,
			maximumItems: 8,
			suggestionsThreshold: 0,
			showAllSuggestions: true,
			fullWidth: true,
			fixed: true,
			preventBrowserAutocomplete: true,
			noCache: true,
			searchFields: ['email'],
			itemClass: 'text-wrap',
			notFoundMessage: '',
			source: (query: string, populateResults: (items: AliasAutocompleteItem[]) => void) => {
				loadAliasCandidates(query).then(populateResults);
			},
			onRenderItem: (item: AliasAutocompleteItem) => item.html,
			onSelectItem: (item: AliasAutocompleteItem) => {
				setSelectedAliasProfile(item);
				setAliasSearchError('');
			},
			onClearItem: () => {
				setSelectedAliasProfile(null);
			},
		};

		const instance = new Autocomplete(input, options);
		instance.getDropMenu().style.zIndex = '2000';
		aliasAutocompleteInstanceRef.current = instance;
		aliasAutocompletePrimedRef.current = false;

		const handleFocus = async () => {
			if (!input.value.trim() && aliasSearchSeed) {
				input.value = aliasSearchSeed;
			}

			setSelectedAliasProfile(null);
			const items = await loadAliasCandidates(input.value || aliasSearchSeed);
			if (!aliasAutocompletePrimedRef.current) {
				instance.setConfig('notFoundMessage', 'No matching profiles found');
				aliasAutocompletePrimedRef.current = true;
			}
			instance.setData(items);
			instance.showOrSearch();
		};

		const handleInput = () => {
			setSelectedAliasProfile(null);
		};

		input.addEventListener('focus', handleFocus);
		input.addEventListener('input', handleInput);

		return () => {
			input.removeEventListener('focus', handleFocus);
			input.removeEventListener('input', handleInput);
			instance.dispose();
			if (aliasAutocompleteInstanceRef.current === instance) {
				aliasAutocompleteInstanceRef.current = null;
			}
		};
	}, [aliasSearchSeed, aliases, loadAliasCandidates]);

	const handleConfirmUnlinkAlias = () => {
		if (!masterId || !aliasToUnlink?.user?.id) {
			return;
		}

		const aliasUserId = aliasToUnlink.user.id;

		unlinkUserAlias(masterId, aliasUserId, token)
			.then(({data}) => {
				toast.success(get(data, 'msg', 'Alias unlinked'));
				processAliases(get(data, 'results.items', []));
			})
			.catch((err) => {
				console.error('Failed to unlink alias', err);
				toast.error(err?.response?.data?.message || 'Failed to unlink alias');
			})
			.finally(() => {
				setShowUnlinkAliasDialog(false);
				setAliasToUnlink(null);
			});
	}

	const getLastLoginLabel = (dateAsIso: string | null): string => {
		if (isNil(dateAsIso)) {
			return 'never';
		}

		try {
			const parsed = parseISO(dateAsIso);
			const now = new Date();
			const minutes = differenceInMinutes(now, parsed);
			if (minutes < 60) {
				return `${Math.max(1, minutes)} min ago`;
			}

			const hours = differenceInHours(now, parsed);
			if (hours < 24) {
				return `${Math.max(1, hours)} hr${hours === 1 ? '' : 's'} ago`;
			}

			const days = differenceInCalendarDays(now, parsed);
			if (days === 1) {
				return 'yesterday';
			}
			return `${days} days ago`;
		} catch (e) {
			return 'unknown';
		}
	}

	const handleResendInvite = () => {
		if (user?.roles?.includes(UserRoleEnum.SuperAdmin)) {
			inviteSuperadmin(user?.email, token)
				.catch(err => {
					console.error(`Error from trying to resend invite to superadmin`, err);
				});
		} else if (user?.roles?.includes(UserRoleEnum.Supervisor)) {
			inviteSupervisor(user?.email, token)
				.catch(err => {
					console.error(`Error from trying to resend invite to supervisor`, err);
				});
		} else {
			throw new Error('Only supporting supervisor/superadmin invites currently');
		}
	};

	const handleSendForgotPassword = () => {
		if (!user?.email) {
			throw new Error('Unable to send forgot password as no email exists');
		}
		return sendForgotPassword(user?.email, token)
			.catch(err => {
				console.error(`Error from trying to send forgot password link`, err);
			});
	};

	const handleUserReplication = () => {
		if (!newAliasFull || !newLocationTenantId || !newAliasRoles) {
			return console.error('Unable to proceed with replicating user as data is missing');
		}

		const payload = {
			email: newAliasFull,
			tenant_id: newLocationTenantId,
			roles: newAliasRoles.split(','),
		};

		replicateUserToNewInstance(+userId, payload, token)
		.then(({data}) => {
			if (data.success) {
				toast.success(data.msg || 'Successfully copied user to new instance');
			} else if (data.error) {
				toast.error(data.msg || 'Failed to copy user to new instance');
			}
		})
		.catch(err => {
			toast.error(err.message || 'Failed to copy user to new instance');
		});
	}

	const handleLinkAlias = () => {
		if (!selectedAliasProfile) {
			return;
		}

		setAliasLinking(true);
		setAliasSearchError('');

		linkUserAlias(masterId || +userId, selectedAliasProfile.userId, token)
			.then(({data}) => {
				setSelectedAliasProfile(null);
				if (aliasAutocompleteRef.current) {
					aliasAutocompleteRef.current.value = '';
				}
				aliasAutocompleteInstanceRef.current?.clear();
				toast.success(get(data, 'msg', 'Alias linked'));
				processAliases(get(data, 'results.items', []));
			})
			.catch((err) => {
				const message = extractReadableApiError(err, 'Failed to link profiles');

				setAliasSearchError(message);
				toast.error(message);
			})
			.finally(() => setAliasLinking(false));
	}

	useEffect(() => {
		if (!!newAliasPrefix && !!newAliasLocationIdentifier && !!newAliasSuffix) {
			setNewAliasFull(newAliasPrefix + newAliasLocationIdentifier + newAliasSuffix);
		} else {
			setNewAliasFull('');
		}
	}, [newAliasPrefix, newAliasLocationIdentifier, newAliasSuffix]);

	return (
		<>
			<PageTitle breadcrumbs={editUserBreadCrumbs}>Edit a User</PageTitle>
			<div className="card edit_user_card">
				<div className="card-header border-0 pt-5"></div>
				<div className="card-body py-3">
					<div className="col-lg-12 text-center">
						<div className="d-flex align-items-center justify-content-center rounded bg-white bg-body shadow w-150px h-150px m-auto">
							<div
								className={`image-input image-input-outline w-120px h-120px ${avatar === '' ? 'image-input-empty' : ''}`}
								data-kt-image-input="true"
								style={{ backgroundImage: `url(${user?.avatarUrl ? user.avatarUrl : toAbsoluteUrl('/media/avatars/blank.png')})` }}
							>
								<div
									className="image-input-wrapper no-shadow shadow-none"
									style={{ backgroundImage: `url(${toAbsoluteUrl(avatar)})` }}
								></div>
								<label
									className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
									data-kt-image-input-action="change"
									data-bs-toggle="tooltip"
									title=""
									data-bs-original-title="Change avatar">
									<i className="bi bi-pencil-fill fs-7"></i>
									<input
										type="file"
										name="avatar"
										accept=".png, .jpg, .jpeg"
										onChange={handleFileChange} />
									<input
										type="hidden"
										name="avatar_remove" />
								</label>
								<span
									className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
									data-kt-image-input-action="remove"
									data-bs-toggle="tooltip"
									title=""
									data-bs-original-title="Remove avatar"
									onClick={removeAvatar}
								>
									<i className="bi bi-x fs-2"></i>
								</span>
							</div>
						</div>
						<div className="form-text mt-5">Set the product thumbnail image. Only
																						*.png, *.jpg and *.jpeg image files
																						are accepted
						</div>
					</div>
					<div className="w-lg-700px p-lg-15 p-10 bg-white rounded mx-auto">
						<form
							className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
							noValidate
							id="kt_login_signup_form"
							onSubmit={formik.handleSubmit}

						>
							<div className="row fv-row mb-7">
								<div className="col-xl-6">
									<div className="fv-row">
										<label className="form-label fw-bolder text-dark fs-6">First Name</label>
										<input
											placeholder="First Name"
											type="text"
											autoComplete="off"
											{...formik.getFieldProps('firstName')}
											className={clsx(
												'form-control form-control-lg form-control-solid',
												{
													'border-danger': formik.touched.firstName && formik.errors.firstName,
												},
												{
													'border-success': formik.touched.firstName && !formik.errors.firstName,
												},
											)}
										/>
										{formik.touched.firstName && formik.errors.firstName && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.firstName}</span>
												</div>
											</div>
										)}
									</div>
								</div>
								<div className="col-xl-6">
									<div className="fv-row">
										<label className="form-label fw-bolder text-dark fs-6">Last Name</label>
										<input
											placeholder="Last Name"
											type="text"
											autoComplete="off"
											{...formik.getFieldProps('lastName')}
											className={clsx(
												'form-control form-control-lg form-control-solid',
												{
													'border-danger': formik.touched.lastName && formik.errors.lastName,
												},
												{
													'border-success': formik.touched.lastName && !formik.errors.lastName,
												},
											)}
										/>
										{formik.touched.lastName && formik.errors.lastName && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.lastName}</span>
												</div>
											</div>
										)}
									</div>
								</div>

								<div className="col-xl-6">
									<div className="fv-row">
										<label className="form-label fw-bolder text-dark fs-6">Phone</label>
										<input
											placeholder="+18005551234"
											type="text"
											autoComplete="off"
											{...formik.getFieldProps('phone')}
											className={clsx(
												'form-control form-control-lg form-control-solid',
												{
													'border-danger': formik.touched.phone && formik.errors.phone,
												},
												{
													'border-success': formik.touched.phone && !formik.errors.phone,
												},
											)}
										/>
										{formik.touched.phone && formik.errors.phone && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.phone}</span>
												</div>
											</div>
										)}
									</div>
								</div>

								<div className="col-xl-6">
									<div className="fv-row">
										<label className="form-label fw-bolder text-dark fs-6">Email</label>
										<input
											placeholder="test.email@secchi.io"
											type="text"
											autoComplete="off"
											{...formik.getFieldProps('email')}
											className={clsx(
												'form-control form-control-lg form-control-solid',
												{
													'border-danger': formik.touched.email && formik.errors.email,
												},
												{
													'border-success': formik.touched.email && !formik.errors.email,
												},
											)}
										/>
										{!!user?.email && (
											<div className="d-flex flex-column">
												<span className="d-block">Current Email:</span>
												<a className="d-block" href={`mailto:${user.email}`}>{user.email}</a>
											</div>
										)}
										{formik.touched.email && formik.errors.email && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.email}</span>
												</div>
											</div>
										)}
									</div>
								</div>

								<div className="col-xl-6">
									<div className="fv-row">
										<label className="form-label fw-bolder text-dark fs-6">New Password</label>
										<input
											type="text"
											autoComplete="off"
											placeholder="Empty"
											{...formik.getFieldProps('password')}
											onChange={e => {
												const pass = e.target.value;
												setHasMinCharacters(size(pass) >= 8);
												setHasLowercase(/[a-z]/.test(pass));
												setHasUppercase(/[A-Z]/.test(pass));
												setHasSpecialCharacters(/[!@#$%^&*]/.test(pass));
												setHasMinNumbers(/\d/.test(pass));
												formik.setFieldValue('password', pass, true);
											}}
											className={clsx(
												'form-control form-control-lg form-control-solid',
												{
													'border-danger': formik.touched.password && formik.errors.password,
												},
												{
													'border-success': formik.touched.password && !formik.errors.password,
												},
											)}
										/>
										{user?.passwordUpdatedAt && (
											<>
												<span className="d-block text-dark">Last Updated: {format(parseYmdDatesToDateObject(user.passwordUpdatedAt), DateFormatUsingDateFns.UsFriendlyTwoYear)}</span>
												<span className="d-block text-dark">Expires: {format(addDays(parseYmdDatesToDateObject(user.passwordUpdatedAt), 180), DateFormatUsingDateFns.UsFriendlyTwoYear)}</span>
											</>
										)}
										<span className="d-block fst-italic">An empty field will not change their password.</span>
										{formik.errors.password && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.password}</span>
													<ul className="list-unstyled">
														<li className={clsx({
															'text-danger': !hasMinCharacters,
															'text-success': hasMinCharacters,
														})}>
															{hasMinCharacters && (<i className="text-success me-2 bi bi-check-lg"></i>)}
															{!hasMinCharacters && (<i className="text-danger me-2 bi bi-x-lg"></i>)}
																8 or more characters
														</li>
														<li className={clsx({
															'text-danger': !hasUppercase,
															'text-success': hasUppercase,
														})}>
															{hasUppercase && (<i className="text-success me-2 bi bi-check-lg"></i>)}
															{!hasUppercase && (<i className="text-danger me-2 bi bi-x-lg"></i>)}
															Uppercase letters
														</li>
														<li className={clsx({
															'text-danger': !hasLowercase,
															'text-success': hasLowercase,
														})}>
															{hasLowercase && (<i className="text-success me-2 bi bi-check-lg"></i>)}
															{!hasLowercase && (<i className="text-danger me-2 bi bi-x-lg"></i>)}
															Lowercase letters
														</li>
														<li className={clsx({
															'text-danger': !hasMinNumbers,
															'text-success': hasMinNumbers,
														})}>
															{hasMinNumbers && (<i className="text-success me-2 bi bi-check-lg"></i>)}
															{!hasMinNumbers && (<i className="text-danger me-2 bi bi-x-lg"></i>)}
															1 or more numbers
														</li>
														<li className={clsx({
															'text-danger': !hasSpecialCharacters,
															'text-success': hasSpecialCharacters,
														})}>
															{hasSpecialCharacters && (<i className="text-success me-2 bi bi-check-lg"></i>)}
															{!hasSpecialCharacters && (<i className="text-danger me-2 bi bi-x-lg"></i>)}
															1 or more special characters. Valid characters include !@#$%^&*
														</li>
													</ul>
												</div>
												<button
													onClick={handleClearPasswordChange}
													className="btn btn-danger">Clear Pass Change</button>
											</div>
										)}
									</div>
								</div>

								<div className="col-xl-6 mt-5">
									<div className="fv-row mb-3">
										<div className="form-check form-check-custom form-check-solid">
											<input
												className="form-check-input"
												type="checkbox"
												id="flexCheckDefault"
												{...formik.getFieldProps('isEmailVerified')}
												checked={formik.values.isEmailVerified}
											/>
											<label
												className="form-check-label fw-bolder text-dark fs-6"
												htmlFor="flexCheckDefault">
												Email Verified
											</label>
										</div>
									</div>

									<div className="fv-row mb-7">
										<div className="form-check form-check-custom form-check-solid">
											<input
												className="form-check-input"
												type="checkbox"
												id="flexCheckDefault"
												{...formik.getFieldProps('isLockedOut')}
												checked={formik.values.isLockedOut}
											/>
											<label
												className="form-check-label fw-bolder text-dark fs-6"
												htmlFor="flexCheckDefault">
												Locked Out
											</label>
										</div>
									</div>

									<div className="fv-row mb-7">
										<span className="fw-bolder me-2 fs-5">Termination Status:</span>
										<span className={clsx('', {
											'text-danger': !isNull(user?.terminated_date),
										})}>
											{!!user?.terminated_date && `Terminated on ${user.terminated_date}`}
											{!user?.terminated_date && 'Active'}
										</span>
									</div>
								</div>
							</div>

							<div className="fv-row mb-7">
								<label className="form-label fw-bolder text-dark fs-6">Role</label>
								<select
									{...formik.getFieldProps('role')}
									className={clsx(
										'form-control form-control-lg form-control-solid',
									)}
									style={{ appearance: 'auto' }}
								>
									<option value=""></option>
									<option value="SecchiAdmin">Secchi Admin</option>
									<option
										disabled={!(get(signedInUser, 'roles', []) as UserRoleEnum[]).includes(UserRoleEnum.SecchiEngineerSenior)}
										value="SecchiAdmin,SecchiEngineerSenior">Secchi Admin & Secchi Senior Engineer</option>
									<option value="SuperAdmin">Super Admin</option>
									<option value="Supervisor">Supervisor</option>
									<option value="Provisioner">Provisioner</option>
									<option value="SuperAdmin,Provisioner">SuperAdmin & Provisioner</option>
								</select>
								{formik.touched.role && formik.errors.role && (
									<div className="fv-plugins-message-container">
										<div className="fv-help-block">
											<span role="alert">{formik.errors.role}</span>
										</div>
									</div>
								)}
							</div>

							<div className="d-flex mt-10">
								{!user?.isEmailVerified && (
									<button
										type="button"
										className="btn btn-lg btn-secondary me-auto"
										onClick={handleResendInvite}
									>
										Resend Invite
									</button>
								)}
								{user?.isEmailVerified && (
									<button
										type="button"
										className="btn btn-lg btn-secondary me-auto"
										onClick={handleSendForgotPassword}
									>
										Send forgot password
									</button>
								)}
								<button
									type="submit"
									className="btn btn-lg btn-primary ms-auto"
									disabled={formik.isSubmitting || !formik.isValid}
								>
									{!loading && <span className="indicator-label">Update</span>}
									{loading && (
										<span
											className="indicator-progress"
											style={{ display: 'block' }}
										>
											Please wait...{' '}
											<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
										</span>
									)}
								</button>
							</div>
						</form>
						<div className="row mb-7 border-top border-dark-subtle pt-2 mt-3">
							<div className="position-relative d-flex align-items-center">
								<span className="position-absolute top-50 start-50 translate-middle fs-3 text-center d-block">
									Aliases
								</span>
								<button
									type="button"
									className="btn btn-sm btn-light-primary ms-auto"
									onClick={() => setAliasesExpanded(!aliasesExpanded)}
								>
									{aliasesExpanded ? 'Collapse' : 'Expand'}
								</button>
							</div>

							{aliasesExpanded && (
								<div className="mt-3">
									{aliasesLoading && <div className="text-muted">Loading aliases…</div>}
									{!aliasesLoading && !aliases && (
										<div className="text-muted">No Aliases</div>
									)}
									{!aliasesLoading && !!aliases && (
																			<>
														<div className="mb-3">
															<div className="d-flex align-items-start gap-3 flex-wrap">
																<div className="flex-grow-1 position-relative overflow-visible" style={{minWidth: 280}}>
																	{(!masterId || masterId === +userId) && (
																			<>
																				<label htmlFor="new-alias-search">Search email to link</label>
																				<input
																					ref={aliasAutocompleteRef}
																					id="new-alias-search"
																					type="search"
																					className="form-control form-control-sm form-control-solid"
																					placeholder="Link Another Profile"
																					autoComplete="off"
																					disabled={(!!masterId && masterId !== +userId) || aliasLinking}
																				/>
																				{!!aliasSearchError && <div className="text-danger fs-8 mt-1">{aliasSearchError}</div>}
																			</>
																		)
																	}
																	{(!!masterId && masterId !== +userId) && (
																		<div className="fw-light fs-8 mt-1">Linking and unlinking is only available from the master profile in this alias group.</div>
																	)}
																</div>
																<button
																	type="button"
																	className="btn btn-outline-info"
																	onClick={handleLinkAlias}
																	disabled={!selectedAliasProfile || aliasLinking || (!!masterId && masterId !== +userId)}
																>
																	{aliasLinking ? 'Linking...' : 'Link Profiles'}
																</button>
															</div>
														</div>
											<div className="table-responsive">
											<table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
												<thead>
													<tr className="fw-bolder fw-bold">
														<th className="text-center">Instance</th>
														<th>Last Login</th>
														<th>Email</th>
														<th className="text-end">Actions</th>
													</tr>
												</thead>
												<tbody>
													{aliases.length === 0 && (
														<tr>
															<td colSpan={4} className="text-muted">
																No aliases found.
															</td>
														</tr>
													)}
													{aliases.map((row) => {
															const rowIsActive = +userId === get(row, 'user.id', undefined);
															// const rowIsActive = isAliasRowActive(row);
															const companyName = (row.user as any)?.company?.name || 'Unknown company';
															const lastLoginValue = getLastLoginLabel(get(row, 'user.last_login', null));

															return (
																<tr key={`${row.user.id}`}>
																	<td>
																				<div className="d-flex align-items-center justify-content-center">
																					<span
																						className={clsx(
																							'badge badge-success rounded-pill fw-bolder fs-8 px-2 py-1 lh-1 me-3',
																							{
																								invisible: !rowIsActive,
																							}
																						)}
																					>
																						active
																					</span>
																					<div className="d-flex flex-column">
																						<span className="fw-bolder">{companyName}</span>
																						{row.is_master && <span className="fw-light">Master Account</span>}
																					</div>
																				</div>
																	</td>

																						<td className="text-nowrap">{lastLoginValue}</td>
																	<td>
																		<a href={`mailto:${row.user.email}`}>{row.user.email}</a>
																	</td>

																	<td>
																		<div className="d-flex justify-content-end align-items-center gap-3 flex-nowrap">
																			{!row.is_master && +userId === masterId && (
																				<>
																					<button
																						type="button"
																						className="btn btn-icon btn-sm p-0"
																						onClick={() => {
																							setAliasToUnlink(row);
																							setShowUnlinkAliasDialog(true);
																						}}
																						aria-label="Unlink alias"
																					>
																						<i className="bi bi-x-square fs-3 text-danger"></i>
																					</button>
																				</>
																		)}
																			{+userId !== row.user.id && (
																				<button
																					type="button"
																					className="btn btn-icon btn-sm p-0"
																					onClick={() => navigate(`/edit-user/${row.user.id}`)}
																					aria-label="View profile"
																				>
																					<i className="bi bi-search fs-3 text-primary"></i>
																				</button>
																			)
																			}
																		</div>
																	</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</div>
																			</>
									)}
								</div>
							)}
						</div>
						<div className="row mb-7 border-top border-dark-subtle pt-2 mt-3">
							<span className="fs-3 text-center d-block">Copy to new instance</span>

							<div>
								<div className="fv-row">

									<label
										htmlFor="edit-user-copy-to-instance"
										className="d-block form-label fw-bolder text-dark fs-6">Select Location</label>
									<select
										id="edit-user-copy-to-instance"
										onChange={evt => {
											setNewLocationTenantId(evt.target.value);
										}}
										defaultValue=""
										className="form-control form-control-lg form-control-solid mt-4">
										<option value="" disabled>Select Location</option>
										{allCompanies.map(cmp => {
											return (
												<option value={cmp.id} key={cmp.id}>{cmp.name}</option>
											)
										})}
									</select>
									<label className="d-block form-label fw-bolder text-dark fs-6">Create friendly alias</label>
									<div className="w-100 d-flex align-content-center justify-content-center">
										<span className="align-content-center">{newAliasPrefix}</span>
										<input
											placeholder="email-friendly location alias"
											type="text"
											autoComplete="off"
											defaultValue=""
											onChange={evt => setNewAliasLocationIdentifier(evt.target.value)}
											className={clsx(
												'form-control form-control-lg form-control-solid d-inline-block',
											)}
										/>
										<span className="align-content-center">{newAliasSuffix}</span>
									</div>
									<span className="text-dark fw-bolder d-block p-5 bg-secondary mt-3">
										New alias will be:
										<a className="ms-2" href={`mailto:${newAliasFull}`}>{newAliasFull}</a>
									</span>

									<div className="row d-flex">
										<label className="form-label fw-bolder text-dark fs-6">Role</label>
										<select
											onChange={evt => setNewAliasRoles(evt.target.value)}
											className={clsx(
												'form-control form-control-lg form-control-solid',
											)}
											style={{ appearance: 'auto' }}
										>
											<option value=""></option>
											<option value="SuperAdmin">SuperAdmin</option>
											<option value="Supervisor">Supervisor</option>
											<option value="Provisioner">Provisioner</option>
											<option value="SuperAdmin,Provisioner">SuperAdmin & Provisioner</option>
										</select>
										{formik.touched.role && formik.errors.role && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">
													<span role="alert">{formik.errors.role}</span>
												</div>
											</div>
										)}
									</div>
									<button
										type="button"
										className="btn btn-lg btn-primary d-inline-block mt-2"
										onClick={handleUserReplication}
										disabled={!newAliasLocationIdentifier || !newLocationTenantId || !newAliasRoles}
									>
										Copy to new instance
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			{showUnlinkAliasDialog && aliasToUnlink && (
				<SkeDialogDelete
					title="Unlink Alias"
									message={
										'Are you sure you want to unlink this profile? This will disassociate this profile from the master account. No data will be deleted, the profile will not be locked out. This will free the profile to be able to be linked to another account.'
									}
					cancelLabel="Cancel"
					confirmLabel="Unlink"
					onCancel={() => {
						setShowUnlinkAliasDialog(false);
						setAliasToUnlink(null);
					}}
					onConfirm={() => handleConfirmUnlinkAlias()}
					successMessage="Alias unlinked"
				/>
			)}
		</>
	);
}

export { EditUser };
